import React, {
  useCallback,
  useRef,
  useState
} from 'react';

import { message } from 'antd';
import cn from 'classnames';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';

import { CustomerAPI } from '@api/customer';
import { THEME } from '@constants/index';
import { TabPane, Tabs } from '@element/Tabs';
import { useIsomorphicLayoutEffect } from '@lib/hooks';
import { store } from '@store/index';
import { getDeviceId, getExecute, helper, I18n } from '@utils/index';
import EmailRegister from '@widget/register/EmailRegister';
import MobileRegister from '@widget/register/MobileRegister';

import { openVerifyCode } from '../VerifyCode';

import styles from './index.module.less';

interface RegsiterProps {
  /** tabClass */
  tabClass?: string;
  /** 底部按钮 方便在H5控制样式 */
  bottomClass?: string;
  /** 注册按钮 class */
  btnClass?: string;
  /** 注册按钮文字 */
  btnText?: string;
  /** 注册成功的回调 */
  cb?: (params: any) => void;
  /** 注册成功前的勾子 */
  beforeHook?: (params: any) => void;
  /** 主题 */
  theme?: Theme;
  /** 邀请码 */
  inviteCode?: string;
  /** 邀请码模块是否展示 默认展示 */
  showInviteCode?: boolean;
  /** 抽屉或弹窗形式 */
  mode?: 'modal' | 'drawer';
  /** 输入验证码模式  modal: 弹窗  page: 跳页面 */
  inputVerifyCodeType?: 'modal' | 'page';
  /** 获取activeKey */
  getActiveKey?: React.Dispatch<React.SetStateAction<string>>;
}

export const Regsiter = observer(
  // eslint-disable-next-line react/display-name
  React.forwardRef<{ activeKey: string }, RegsiterProps>((props, ref) => {
    const {
      showInviteCode,
      tabClass,
      cb,
      beforeHook,
      inviteCode,
      btnText,
      btnClass,
      bottomClass,
      theme,
      mode,
      inputVerifyCodeType = 'modal',
      getActiveKey
    } = props;
    const [activeKey, setActiveKey] = useState('1');
    const [registering, setRegistering] = useState(false);
    const [emailValue, setemailValue] = useState('');
    const [phoneValue, setphoneValue] = useState('');
    const typeRef = useRef(null);
    const dataRef = useRef(null);
    const router = useRouter();

    useIsomorphicLayoutEffect(() => {
      getActiveKey?.(activeKey)
    }, [activeKey])

    useIsomorphicLayoutEffect(() => {
      const registerHome = sessionStorage.getItem('register_home');
      if (registerHome) {
        const { value, type } = JSON.parse(registerHome);
        setActiveKey(type);
        if (type === '1') {
          setphoneValue(value);
        } else {
          setemailValue(value);
        }
      }
      return () => {
        sessionStorage.removeItem('register_home');
      };
    }, []);

    useIsomorphicLayoutEffect(() => {
      if (!router?.isReady) return;
      typeRef.current = router.query?.type || ''
    }, [router?.isReady]);

    function onTabChange (newKey) {
      setActiveKey(newKey);
      setRegistering(false);
    }

    const onFull = async () => {
      const {
        mobile,
        telephoneCode,
        email,
        pwd,
        country,
        inviteCode,
        verifyToken = ''
      } = dataRef.current;
      const deviceId = await getDeviceId();
      const md5Pwd = helper.md5(pwd);
      const registerParams = {
        email,
        mobile: mobile ? encodeURIComponent(telephoneCode) + mobile : '',
        password: md5Pwd,
        rpassword: md5Pwd,
        country,
        gender: 2,
        verifyToken,
        referee: inviteCode,
        activityType: 18, // ??
        tokenType: 'web', // ??
        deviceId,
        inviteCode
      };
      const result = await CustomerAPI.register(registerParams);
      const userInfo = result.obj;
      store.userStore.loginSuccess(userInfo);
      store.userStore.getUserSocialInfo({
        userId: userInfo.id,
        token: userInfo.jwtKey
      });
      cb?.({ form: dataRef.current, res: userInfo });
      setRegistering(false);
    };
    /** 发送验证码 */
    const sendCode = async () => {
      const data = await getExecute();
      const { mobile, telephoneCode, email } = dataRef.current;
      const params = {
        type: 0,
        businessType: 0,
        [mobile ? 'mobile' : 'email']: mobile
          ? encodeURIComponent(telephoneCode) + mobile
          : email
      };
      beforeHook?.({ form: dataRef.current });
      const response = await CustomerAPI.sendVerifyCode(params, data);
      if (response.code === '0105') {
        message.success(I18n.t('register_code_send_successfully'), 2);
        return true;
      }
      let msg = 'system.error';
      if (response.msgKey) {
        msg = response.msgKey;
      }
      if (
        response.msgKey === 'customer.phone.duplicate' ||
        response.msgKey === 'customer.reg.email.duplicate'
      ) {
        msg = mobile ? 'mobileRegistered' : 'emailRegistered';
      }
      message.error(I18n.t(msg), 2);
      return false;
    };

    /** 校验验证码 */
    const onVerifyCode = async (
      value
    ): Promise<{ errorText?: string; status?: 'DONE' }> => {
      const { mobile, telephoneCode, email } = dataRef.current;
      const params = {
        mobile: mobile ? encodeURIComponent(telephoneCode) + mobile : '',
        email: email,
        verifyCode: value,
        type: 0
      };
      const response = await CustomerAPI.verifyCode(params);
      if (!response.success) return { errorText: I18n.t('WLH0kO0a') };
      dataRef.current.verifyToken = response.obj;
      return {
        status: 'DONE'
      };
    };

    const getCode = async (): Promise<any> => {
      const { mobile, telephoneCode, email } = dataRef.current;
      const mobileText = `${I18n.t('mobile')}${helper.hiddenMobileText(
        mobile,
        telephoneCode
      )}`;
      const emailText = `${I18n.t('email')}${helper.hiddenEmailText(email)}`;
      const typeText = mobile ? mobileText : emailText;
      const code = await openVerifyCode({
        title: mobile
          ? I18n.t('register_mobile_verify')
          : I18n.t('register_email_verify'),
        subTitle: `${I18n.t('register_code_send_to')}${typeText}`,
        onComplete: onVerifyCode,
        theme,
        mode,
        onSend: sendCode
      });
      return code;
    };
    const onFinishNoCaptcha = async () => {
      const res = await sendCode();
      if (!res) return setRegistering(false);
      // closeModal();
      const code = await getCode();
      if (code !== 'cancel') return onFull();
      setRegistering(false);
    };
    const toRegisterModal = async (params) => {
      dataRef.current = {
        ...params,
        telephoneCode: `+${params.countryNum}`
      };

      onFinishNoCaptcha();
    };

    const toRegisterPage = useCallback((params) => {
      const { mobile, email, countryNum, inviteCode, country, pwd } = params;
      const queryParams = {
        email,
        mobile,
        inviteCode,
        country,
        telephoneCode: countryNum,
        type: typeRef.current
      };
      sessionStorage.setItem('registerPassword', pwd);
      router.push({
        pathname: '/register/verifyCode',
        query: queryParams
      });
    }, [router]);

    const toRegister = (params) => {
      const toRegisterMap = {
        modal: toRegisterModal,
        page: toRegisterPage
      };
      toRegisterMap[inputVerifyCodeType](params);
    };

    const handleSubmit = useCallback(async (params) => {
      setRegistering(true);
      const { inviteCode } = params;
      try {
        if (inviteCode) {
          const inviteResponse = await CustomerAPI.verifyRebateCode({
            referee: inviteCode
          });
          if (inviteResponse.success) return toRegister(params);
          setRegistering(false);
          message.error(I18n.t(inviteResponse.msgKey), 2);
          return;
        }
        toRegister(params);
      } catch (e) {
        setRegistering(false);
        console.log('error', e);
      }
    }, []);
    return (
      <div className={cn({ [styles.dark_container]: theme === THEME.DARK })}>
        <Tabs
          onChange={onTabChange}
          activeKey={activeKey}
          type="login"
          theme={theme}
          className={cn(styles.overflow, tabClass)}
        >
          <TabPane tab={I18n.t('mobile')} key="1" type="login" theme={theme}>
            <MobileRegister
              activeKey={activeKey}
              handleSubmit={handleSubmit}
              registering={registering}
              initValue={phoneValue}
              ic={inviteCode}
              btnText={btnText}
              btnClass={btnClass}
              bottomClass={bottomClass}
              showInviteCode={showInviteCode}
              theme={theme}
            />
          </TabPane>
          <TabPane tab={I18n.t('email')} key="2" type="login">
            <EmailRegister
              activeKey={activeKey}
              handleSubmit={handleSubmit}
              registering={registering}
              initValue={emailValue}
              ic={inviteCode}
              btnText={btnText}
              btnClass={btnClass}
              bottomClass={bottomClass}
              showInviteCode={showInviteCode}
              theme={theme}
            />
          </TabPane>
        </Tabs>
      </div>
    );
  })
);
